import { FC, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { UseMutationResult } from '@tanstack/react-query';
import {
  KidTemplateSetupStatus,
  KidTemplateType,
} from '../../../../../api/kids/types';
import { useUpdateKidTemplateName } from '../../../../../api/kids/hooks/mutations';
import {
  extractDetail,
  isConflictError,
} from '../../../../../utilities/QueryHelpers';
import useConfirmation from '../../../../../hooks/useConfirmation';
import GenericModal from '../../../../ui/GenericModal';
import {
  btnPrimary,
  btnSecondary,
  ENABLE_TEMPLATE_TYPE_CHANGE,
  formDropdownFullBleedClassName,
  formatCreateTemplateSubtitle,
  formatKidTemplateTypeLabel,
  getTemplateReferenceFieldLabel,
  inputClassName,
  inputErrorClassName,
  normalizeTemplateSetupStatus,
} from '../templateManagement.constants';
import { TemplateReferenceOptions } from '../templateManagement.types';
import TemplateFormDropdown from './TemplateFormDropdown';
import TemplateReferenceField from './TemplateReferenceField';
import TemplateTypeChip from './TemplateTypeChip';
import { cn } from '../../../../../utilities/cn';

type DetailsFormValues = {
  name: string;
  type: KidTemplateType;
  referenceId: string;
};

const EditTemplateDetailsModal: FC<
  TemplateReferenceOptions & {
    isOpen: boolean;
    onClose: () => void;
    clientId: string;
    templateId: string;
    templateName: string;
    currentTemplateType: KidTemplateType | null;
    currentScopeLabel?: string | null;
    allowedDowngradeTypes: KidTemplateType[];
    templateSetupStatus?: string | null;
    lockScopeChanges: boolean;
    downgradeMutation: UseMutationResult<any, any, any>;
  }
> = ({
  isOpen,
  onClose,
  clientId,
  templateId,
  templateName,
  currentTemplateType,
  currentScopeLabel,
  allowedDowngradeTypes,
  templateSetupStatus,
  lockScopeChanges,
  downgradeMutation,
  ...referenceOptions
}) => {
  const renameMutation = useUpdateKidTemplateName(clientId, templateId);
  const { handleConfirmation } = useConfirmation();
  const selectableTypes = useMemo(() => {
    if (!currentTemplateType) return allowedDowngradeTypes;
    return [
      currentTemplateType,
      ...allowedDowngradeTypes.filter((type) => type !== currentTemplateType),
    ];
  }, [allowedDowngradeTypes, currentTemplateType]);

  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<DetailsFormValues>({
    defaultValues: {
      name: templateName,
      type: currentTemplateType ?? selectableTypes[0] ?? KidTemplateType.MANCO,
      referenceId: '',
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    reset({
      name: templateName,
      type: currentTemplateType ?? selectableTypes[0] ?? KidTemplateType.MANCO,
      referenceId: '',
    });
    // Reset only when the modal opens so dropdown edits are not wiped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const selectedType = watch('type');
  const referenceId = watch('referenceId');
  const name = watch('name');

  useEffect(() => {
    setValue('referenceId', '');
  }, [selectedType, setValue]);

  const {
    onBlur: onNameBlur,
    onChange: onNameChange,
    ...nameField
  } = register('name', {
    validate: (value) =>
      value.trim().length > 0 || 'Please enter a template name.',
    setValueAs: (value) => (typeof value === 'string' ? value.trim() : value),
  });

  const handleRenameConflict = (error: unknown) => {
    if (!isConflictError(error)) return;

    setError('name', {
      type: 'server',
      message:
        extractDetail(error) ?? 'A template with this name already exists.',
    });
  };

  const typeChanged =
    ENABLE_TEMPLATE_TYPE_CHANGE &&
    Boolean(currentTemplateType) &&
    selectedType !== currentTemplateType;
  const nameChanged = name.trim() !== (templateName || '').trim();
  const showReferenceField = selectedType !== KidTemplateType.MANCO && typeChanged;
  const typeChangeValid =
    !typeChanged ||
    (!lockScopeChanges &&
      selectableTypes.includes(selectedType) &&
      (selectedType === KidTemplateType.MANCO || !!referenceId));
  const isSaving = renameMutation.isPending || downgradeMutation.isPending;
  const canSave =
    name.trim().length > 0 &&
    (nameChanged || typeChanged) &&
    typeChangeValid &&
    !isSaving;

  const applyTypeChange = () => {
    if (!typeChanged) {
      onClose();
      return;
    }
    const { type, referenceId: nextReferenceId } = getValues();
    downgradeMutation.mutate(
      {
        updated_template_type: type,
        ...(type !== KidTemplateType.MANCO && nextReferenceId
          ? { updated_reference_id: nextReferenceId }
          : {}),
      },
      { onSuccess: () => onClose() },
    );
  };

  const persistDetails = () => {
    const nextName = getValues('name').trim();
    if (!nextName) return;

    if (nameChanged) {
      renameMutation.mutate(
        { template_name: nextName },
        {
          onSuccess: applyTypeChange,
          onError: handleRenameConflict,
        },
      );
      return;
    }

    applyTypeChange();
  };

  const handleSave = () => {
    const nextName = getValues('name').trim();
    if (!nextName || !canSave) return;

    handleConfirmation(
      typeChanged ? 'Change template type' : 'Save template details',
      typeChanged ? (
        <div className="space-y-3">
          {nameChanged ? (
            <p>
              The template will be renamed to{' '}
              <span className="font-semibold">{nextName}</span>.
            </p>
          ) : null}
          <p>
            Narrowing the type creates new templates for that scope and
            deactivates this one. You will return to the template list.
          </p>
        </div>
      ) : (
        <p>
          Rename this template to{' '}
          <span className="font-semibold">{nextName}</span>?
        </p>
      ),
      persistDetails,
      typeChanged ? { confirmColor: 'error' } : undefined,
    );
  };

  const typeLocked = lockScopeChanges || selectableTypes.length <= 1;
  const typeChipTooltip = (templateType: KidTemplateType) =>
    templateType === currentTemplateType && currentScopeLabel
      ? currentScopeLabel
      : formatCreateTemplateSubtitle(templateType);
  const setupStatus = normalizeTemplateSetupStatus(templateSetupStatus);
  const isInvalidSetup = setupStatus === KidTemplateSetupStatus.INVALID;
  const isIncompleteSetup =
    setupStatus === KidTemplateSetupStatus.INCOMPLETE ||
    (lockScopeChanges && !isInvalidSetup);
  const typeSectionTone = isInvalidSetup
    ? {
        section: 'rounded-lg border border-rose-200 bg-rose-50 p-3',
        title: 'text-sm font-semibold text-rose-950',
        dot: 'text-rose-300',
        helper: 'text-xs leading-snug text-rose-800',
      }
    : isIncompleteSetup
      ? {
          section: 'rounded-lg border border-amber-200 bg-amber-50 p-3',
          title: 'text-sm font-semibold text-amber-950',
          dot: 'text-amber-300',
          helper: 'text-xs leading-snug text-amber-800',
        }
      : {
          section: 'rounded-lg border border-gray-200 bg-gray-50/70 p-3',
          title: 'text-sm font-semibold text-gray-900',
          dot: 'text-gray-300',
          helper: 'text-xs leading-snug text-gray-500',
        };
  const typeLockCopy = isInvalidSetup
    ? 'Scope editing is locked until this template is removed from documents it should not appear on.'
    : lockScopeChanges
      ? 'Scope editing is locked until this template is on every document it should appear on.'
      : 'You can only switch to a narrower type.';

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) onClose();
      }}
      title="Edit template"
      className="max-w-xl"
      closeOnOutsideClick={!ENABLE_TEMPLATE_TYPE_CHANGE}
    >
      <div className="space-y-4">
        <div className="flex min-w-0 flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">Name</label>
          <input
            {...nameField}
            onChange={(event) => {
              if (errors.name?.type === 'server') {
                clearErrors('name');
              }
              onNameChange(event);
            }}
            onBlur={(event) => {
              onNameBlur(event);
              const trimmed = event.target.value.trim();
              if (trimmed !== event.target.value) {
                setValue('name', trimmed, { shouldValidate: true });
              }
            }}
            placeholder="Template name"
            className={cn(inputClassName, errors.name && inputErrorClassName)}
          />
          {errors.name?.message ? (
            <p className="text-xs text-red-600">{errors.name.message}</p>
          ) : (
            <p className="text-xs text-gray-500">
              Identifier only. This does not change document content.
            </p>
          )}
        </div>

        {ENABLE_TEMPLATE_TYPE_CHANGE ? (
          <section className={typeSectionTone.section}>
            <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
              <h3 className={typeSectionTone.title}>Type</h3>
              <span className={typeSectionTone.dot} aria-hidden>
                ·
              </span>
              <p className={typeSectionTone.helper}>{typeLockCopy}</p>
            </div>

            <div className="mt-3 space-y-3">
              <div className="flex min-w-0 flex-col gap-1">
                <label className="text-xs font-medium text-gray-700">
                  Template type
                </label>
                {typeLocked && currentTemplateType ? (
                  <div className="flex min-h-[2.25rem] items-center rounded-md border border-gray-200 bg-white px-3 py-1.5">
                    <TemplateTypeChip
                      templateType={currentTemplateType}
                      size="sm"
                      tooltip={typeChipTooltip(currentTemplateType)}
                    />
                  </div>
                ) : (
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <TemplateFormDropdown
                        options={selectableTypes.map((templateType) => ({
                          id: templateType,
                          label: formatKidTemplateTypeLabel(templateType),
                        }))}
                        value={field.value}
                        onChange={(value) =>
                          field.onChange(value as KidTemplateType)
                        }
                        placeholder="Select type"
                        widthClassName={formDropdownFullBleedClassName}
                        tooltip="Template type"
                        renderOption={(option) => (
                          <TemplateTypeChip
                            templateType={option.id}
                            size="sm"
                            tooltip={typeChipTooltip(
                              option.id as KidTemplateType,
                            )}
                          />
                        )}
                        renderLabel={(option) =>
                          option ? (
                            <TemplateTypeChip
                              templateType={option.id}
                              size="sm"
                              tooltip={typeChipTooltip(
                                option.id as KidTemplateType,
                              )}
                            />
                          ) : (
                            'Select type'
                          )
                        }
                      />
                    )}
                  />
                )}
              </div>

              {showReferenceField ? (
                <div className="flex min-w-0 flex-col gap-1">
                  <label className="text-xs font-medium text-gray-700">
                    {getTemplateReferenceFieldLabel(selectedType)}
                  </label>
                  <Controller
                    name="referenceId"
                    control={control}
                    render={({ field }) => (
                      <TemplateReferenceField
                        templateType={selectedType}
                        value={field.value}
                        onChange={field.onChange}
                        controlOnly
                        dropdownWidthClassName={formDropdownFullBleedClassName}
                        {...referenceOptions}
                      />
                    )}
                  />
                </div>
              ) : null}

              {typeChanged ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs leading-snug text-amber-950">
                  Narrowing the type creates new templates for that scope and
                  deactivates this one.
                </p>
              ) : null}
            </div>
          </section>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className={btnSecondary} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={btnPrimary}
            onClick={handleSave}
            disabled={!canSave}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </GenericModal>
  );
};

export default EditTemplateDetailsModal;
